import {TextInput } from "components/core";
import { useFormik } from "formik";
import {FC, useCallback} from "react";
import {wrapClick, classNames, parseExcelWithSchema} from "utils";
import { InfoFormSchema, IRegularizationMeterRetirementSetFormSchema } from "./schema";
import { DistrictPicker } from "containers";
import lodash from "lodash";
import {useDropzone} from "react-dropzone";
import {Disclosure} from "@headlessui/react";
import {ChevronUpIcon} from "@heroicons/react/24/outline";


export const headerMap = {
  sn: "SN",
  serviceOrderCode: "SERVICE ORDER CODE",
};

// Generate Schema for Excel Reader
const ExcelSchema = lodash.chain(headerMap)
  .entries()
  .map(([key, val]) => ([
    key,
    {
      prop: key,
      column: val,
      type: String
    }
  ]))
  .fromPairs()
  .value()

export const columns = Object.keys(headerMap);


interface InfoFormProps {
  handleNext: (values: IRegularizationMeterRetirementSetFormSchema["info"]) => void;
  handlePrevious: () => void;
  initialValues: IRegularizationMeterRetirementSetFormSchema["info"];
  values: IRegularizationMeterRetirementSetFormSchema;
  handleCancel: () => void;
}

const InfoForm: FC<InfoFormProps> = ({
  initialValues,
  handleNext,
  handleCancel,
}) => {
  const form = useFormik<IRegularizationMeterRetirementSetFormSchema["info"]>({
    initialValues,
    validationSchema: InfoFormSchema,
    onSubmit: (values) => {
      handleNext(values);
    },
    onReset: () => {
      handleCancel?.();
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Do something with the files
    const file = acceptedFiles[0];

    type RowEntry = Record<keyof typeof headerMap, string>

    parseExcelWithSchema<RowEntry>(file, ExcelSchema, ({ rows, errors }) => {

      if(errors) {
        errors.map((err) => form.setFieldError(`requestCodes.${err.row}`, err.error));
      }
      form.setFieldValue(`requestCodes`, rows?.map(row => row.serviceOrderCode));
    }).then()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    getRootProps,
    getInputProps,
    acceptedFiles,
    open: openSelect,
  } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "text/csv": [".csv"],
      "text/xlsx": [".xlsx"],
      "text/xls": [".xls"],
      "text/json": [".json"],
    },
  });



  return (
    <div className='flex-1 flex flex-col overflow-hidden'>
      <div className='space-y-6 divide-y divide-gray-200 p-6 flex-1 overflow-y-auto'>
        <div className='grid grid-cols-4 gap-4'>
          <div>
            <DistrictPicker
              id='district'
              disabled
              label='District'
              rawId
              {...form}
            />
          </div>
          <div className='col-span-3'>
            <TextInput
              id='description'
              placeholder='Description'
              label='Description'
              {...form}
            />
          </div>
        </div>
        <div className='pt-6'>
          <span className='text-xs font-light'>
             Regularization Installation Service Orders Information
          </span>
          <div className='grid grid-cols-3 gap-6 mt-2'>
            {!form.isValid && Boolean(lodash.get(form.errors, `requestCodes`)) && (
              <div className={"col-span-3"}>
                <Disclosure>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full  justify-between mt-4 bg-red-100 px-4 py-2 text-left text-sm font-medium text-red-900 hover:bg-red-200 focus:outline-none focus-visible:ring focus-visible:ring-red-500 focus-visible:ring-opacity-75">
                        <span>Errors Detected</span>
                        <ChevronUpIcon
                          className={`${!open ? "rotate-180 transform" : ""
                          } h-5 w-5 text-red-500`}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="w-full px-4 pt-4 pb-2  text-sm text-gray-500 overflow-y-scroll no-scrollbar bg-red-100">
                        <div className="overflow-y-">
                          {lodash.isArray(lodash.get(form.errors, `requestCodes`)) ? (
                            (lodash.get(form.errors, `requestCodes`) as string[])?.map((row: any, rowIndex: number) => (
                              <div className="text-red-700">
                                <span className="font-bold">Row: {rowIndex + 1}</span>
                                {lodash.isString(row) ? (
                                  <span>{row}</span>
                                ) : (
                                  <div className="flex flex-col">
                                    {row?.sn ? <li className={'list-none'}>{row?.sn}</li> : null}
                                    {row?.serviceOrderCode ? <li className={'list-none'}>{row?.serviceOrderCode}</li> : null}
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <span>{lodash.get(form?.errors, `requestCodes`)}</span>
                          )}
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>
            )}
            <div
              className={classNames(
                "col-span-3 flex-1 flex flex-col bg-white text-left  overflow-y-auto transform transition-all",
              )}
            >
              <div {...getRootProps()} className="flex flex-col flex-1 overflow-x-auto no-scrollbar ">
                {!acceptedFiles.length && (
                  <button
                    type="button"
                    className="relative h-full w-full flex-1 block rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m0-4c0 4.418-7.163 8-16 8S8 28.418 8 24m32 10v6m0 0v6m0-6h6m-6 0h-6"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No file selected
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Kindly select a file by clicking the button above.
                    </p>
                  </button>
                )}
                <input
                  {...getInputProps()}
                  id="members"
                  name="members"
                  type="file"
                  className="sr-only"
                />
                {(acceptedFiles.length > 0 || form.values.requestCodes?.length > 0) && (
                  <div className="my-6 max-h-[500px] overflow-y-auto">
                    <table className="min-w-full  divide-y divide-gray-200 overflow-x-scroll">
                      <thead className="bg-gray-50 sticky top-0 z-10 border-b">
                      <tr>
                        {columns?.map((column: any, idx: number) => (
                          <th
                            scope="col"
                            key={idx}
                            className={classNames(
                              idx === 0 ? "sticky left-0 bg-gray-50" : "",
                              "px-6 py-3 mt-0 text-left text-xs font-medium whitespace-nowrap text-gray-500 uppercase tracking-wider"
                            )}
                          >
                            {lodash.get(headerMap, column)}
                          </th>
                        ))}
                      </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                      {form.values?.requestCodes?.map((item, key) => {
                        return (
                          <tr key={key}>
                            <td
                              className={classNames(
                                "sticky left-0 bg-gray-50",
                                "px-6 py-4 whitespace-nowrap text-sm ",
                                "text-gray-500"
                              )}
                            >
                              {key + 1}
                            </td>
                            <td
                              className={classNames(
                                "px-6 py-4 whitespace-nowrap text-sm ",
                                "text-gray-500"
                              )}
                            >
                              {item}
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </table>
                  </div>
                )}
                {(!!acceptedFiles.length || form.values.requestCodes?.length > 0)  && (
                  <button
                    type="button"
                    onClick={wrapClick(openSelect)}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-600 text-base font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Select New File
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200'>
        <button
          type='button'
          onClick={wrapClick(form.handleSubmit)}
          className='w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm'
        >
          Next
        </button>
        <button
          type='button'
          className='mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
          onClick={wrapClick(form.resetForm)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default InfoForm;
